import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";
import { useQuery } from "@tanstack/react-query";

import {
  apiGetPatientsByPatientIdGdmtOptions,
  GDMTConflictReason,
  type GDMTMedicationResult,
  GDMTNotApplicableReason,
  type GDMTResult,
} from "../../../api";

/**
 * Fetches the GDMT (guideline-directed medical therapy) score for a patient.
 * Returns `undefined` on any failure — the endpoint is not deployed on every
 * environment yet — so that the pages embedding the score hide it instead of
 * surfacing an error or hanging on `loading`. Patients without an HF type are
 * answered with a regular `not_applicable` result, not an error.
 */
export function useGdmt(patientId: number): {
  result?: GDMTResult;
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const query = useQuery({
    ...apiGetPatientsByPatientIdGdmtOptions({
      path: { patient_id: patientId },
    }),
    meta: { handlesOwnErrors: true },
  });

  return {
    result: query.data,
    loading: query.isPending,
    refetch: async () => {
      await query.refetch();
    },
  };
}

export function optimizedPercent(result: GDMTResult): number {
  if (result.gdmt_max <= 0) return 0;
  return Math.round((result.gdmt_score / result.gdmt_max) * 100);
}

/** Ring/emphasis color derived from how optimized the overall therapy is. */
export function optimizationColor(percent: number): {
  stroke: string;
  fill: string;
  text: string;
} {
  if (percent < 34)
    return {
      stroke: "stroke-red-600",
      fill: "fill-red-600",
      text: "text-red-600",
    };
  if (percent < 67)
    return {
      stroke: "stroke-amber-500",
      fill: "fill-amber-500",
      text: "text-amber-500",
    };
  return {
    stroke: "stroke-green-600",
    fill: "fill-green-600",
    text: "text-green-600",
  };
}

/**
 * What the record still needs before a score can be computed, or `undefined`
 * when the score is ready. Both surfaces show this instead of the score, so a
 * patient who can't be scored says why rather than rendering nothing.
 *
 * An empty medication list is not one of those cases: with an HF type on file
 * the API scores that patient 0, and a patient on none of the four pillars is
 * exactly the gap in care the tracker exists to surface.
 */
export function missingForScore(
  result: GDMTResult,
): MessageDescriptor | undefined {
  return result.not_applicable ? notApplicableText(result) : undefined;
}

/** Why no score applies, with a generic fallback when the API doesn't say. */
function notApplicableText(result: GDMTResult): MessageDescriptor {
  const reason = result.not_applicable_reason;
  return reason == null
    ? msg`Not applicable for this patient`
    : notApplicableReason[reason];
}

// `GDMTNotApplicableReason` is generated without member names because the API
// spec omits `x-enum-varnames` for it, so name its values here. This alias is
// the only record of which integer means what, so keep the mapping keyed by it
// rather than by a comparison — a `Record` turns a new API member into a build
// failure instead of silently falling back to the generic text.
const noHfType = GDMTNotApplicableReason._0;

const notApplicableReason: Record<GDMTNotApplicableReason, MessageDescriptor> =
  {
    [noHfType]: msg`Needs an HF type diagnosis`,
  };

/**
 * Short, human-readable hint for a per-medication conflict flag. `undefined`
 * when there is no flag — and also when the API sends a reason the generated
 * types predate, since the server can be deployed ahead of the frontend.
 *
 * The parameter is derived from the contract field rather than spelled out as
 * `GDMTConflictReason | null | undefined`: environments whose API spec predates
 * GDMT generate no such type, and a union naming an unresolvable type trips
 * `no-redundant-type-constituents` (it lints against the production spec).
 */
export function flagHint(
  flag: GDMTMedicationResult["flag"],
): MessageDescriptor | undefined {
  return flag == null ? undefined : flagHints[flag];
}

const flagHints: Record<GDMTConflictReason, MessageDescriptor> = {
  [GDMTConflictReason.medication_in_other_group]: msg`Filed under Other — reclassify to score`,
  [GDMTConflictReason.medication_not_gdmt_relevant]: msg`Not part of GDMT scoring`,
  [GDMTConflictReason.medication_target_dosage_not_in_data]: msg`No target dose on file`,
  [GDMTConflictReason.medication_does_not_match_patient_hf_type]: msg`Doesn't match patient HF type`,
  [GDMTConflictReason.multiple_medications_same_class]: msg`Multiple drugs in this class — highest counted`,
  [GDMTConflictReason.multiple_drug_classes_in_raas_slot]: msg`Multiple RAAS classes — highest-priority counted`,
  [GDMTConflictReason.patient_weight_missing]: msg`Target assumes lowest weight band`,
};
